import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Users, Target, TrendingUp, BarChart3, DollarSign, Mail,
  MessageSquare, HeadphonesIcon, Heart, Settings, FileText,
  ShoppingBag, Megaphone, QrCode, CheckCircle2, Clock,
  RefreshCw, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import { useCurrency } from '../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── KPI Card ───
const KPI = ({ label, value, sub, icon: Icon, iconColor, trend, className }) => (
  <Card className={cn("bg-white border-gray-border", className)} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-[11px] sm:text-xs text-gray-medium uppercase tracking-wide">{label}</p>
          <p className="text-lg sm:text-2xl font-bold text-text-primary mt-0.5">{value}</p>
          {sub && <p className="text-[10px] sm:text-xs text-gray-medium mt-0.5">{sub}</p>}
          {trend !== undefined && (
            <div className={cn("flex items-center gap-0.5 text-[10px] mt-1", trend >= 0 ? "text-emerald-600" : "text-red-500")}>
              {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        {Icon && (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${iconColor}15` }}>
            <Icon className="h-4 w-4" style={{ color: iconColor }} />
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// ─── Mini Bar ───
const MiniBar = ({ items, colorMap }) => {
  const max = Math.max(...items.map(i => i.count || i.value || 0), 1);
  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const val = item.count || item.value || 0;
        const label = item.stage || item.source || item.department || item.type || item.month || item.label || 'Unknown';
        const color = colorMap?.[i % (colorMap?.length || 1)] || '#10B981';
        return (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] sm:text-xs text-gray-medium w-20 sm:w-24 truncate capitalize">{label}</span>
            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(val / max) * 100}%`, backgroundColor: color }} />
            </div>
            <span className="text-[10px] sm:text-xs font-medium text-text-primary w-8 text-right">{val}</span>
          </div>
        );
      })}
    </div>
  );
};

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

// ─── Section Dashboard ───
const SectionDashboard = ({ title, icon: Icon, iconColor, data, type }) => {
  const { format: fc } = useCurrency();
  if (!data) return null;

  const renderContent = () => {
    switch (type) {
      case 'lead-generation':
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Total Contacts" value={data.total_contacts} icon={Users} iconColor="#10B981" trend={data.growth_pct} />
              <KPI label="New This Month" value={data.new_this_month} icon={Target} iconColor="#3B82F6" />
              <KPI label="Conversion Rate" value={`${data.conversion_rate}%`} icon={TrendingUp} iconColor="#8B5CF6" />
              <KPI label="Lead Forms" value={data.total_forms} icon={FileText} iconColor="#F59E0B" sub={`${data.total_submissions} submissions`} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Lead Stages</CardTitle></CardHeader>
                <CardContent>
                  <MiniBar items={Object.entries(data.by_stage || {}).map(([k, v]) => ({ label: k, count: v }))} colorMap={COLORS} />
                  {Object.keys(data.by_stage || {}).length === 0 && <p className="text-xs text-gray-medium text-center py-2">No data</p>}
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Lead Sources</CardTitle></CardHeader>
                <CardContent>
                  <MiniBar items={data.by_source || []} colorMap={COLORS} />
                  {(data.by_source || []).length === 0 && <p className="text-xs text-gray-medium text-center py-2">No data</p>}
                </CardContent>
              </Card>
            </div>
          </>
        );

      case 'sales':
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Total Deals" value={data.total_deals} icon={DollarSign} iconColor="#10B981" />
              <KPI label="Pipeline Value" value={fc(data.pipeline_value || 0)} icon={TrendingUp} iconColor="#3B82F6" sub={`${data.active_deals} active`} />
              <KPI label="Win Rate" value={`${data.win_rate}%`} icon={Target} iconColor="#8B5CF6" sub={`${data.won_deals}W / ${data.lost_deals}L`} />
              <KPI label="Proposals" value={data.total_proposals} icon={FileText} iconColor="#F59E0B" sub={`${data.proposal_acceptance_rate}% accepted`} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Deal Stages</CardTitle></CardHeader>
                <CardContent>
                  <MiniBar items={(data.by_stage || []).map(s => ({ label: s.stage, count: s.count }))} colorMap={COLORS} />
                  {(data.by_stage || []).length === 0 && <p className="text-xs text-gray-medium text-center py-2">No data</p>}
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Revenue Summary</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-xs font-medium text-emerald-700">Revenue Won</span>
                    <span className="text-sm font-bold text-emerald-700">{fc(data.revenue_won || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-xs font-medium text-blue-700">Products</span>
                    <span className="text-sm font-bold text-blue-700">{data.active_products}/{data.total_products}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        );

      case 'marketing':
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Email Campaigns" value={data.email_campaigns} icon={Mail} iconColor="#3B82F6" sub={`${data.email_subscribers} subscribers`} />
              <KPI label="Social Posts" value={data.social_posts} icon={Megaphone} iconColor="#8B5CF6" sub={`${data.published_posts} published`} />
              <KPI label="Ad Campaigns" value={data.ad_campaigns} icon={Target} iconColor="#F59E0B" sub={`${fc(data.total_ad_spend || 0)} spend`} />
              <KPI label="SMS/WhatsApp" value={(data.sms_sent_this_month || 0) + (data.whatsapp_sent_this_month || 0)} icon={MessageSquare} iconColor="#10B981" sub="This month" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              <Card className="bg-white border-gray-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-text-primary">{data.total_ad_clicks?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-medium">Total Ad Clicks</p>
                  <Progress value={Math.min((data.total_ad_conversions || 0) / Math.max(data.total_ad_clicks || 1, 1) * 100, 100)} className="mt-2 h-2" />
                  <p className="text-[10px] text-gray-medium mt-1">{data.total_ad_conversions || 0} conversions</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-text-primary">{data.social_accounts || 0}</p>
                  <p className="text-xs text-gray-medium">Connected Social Accounts</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-text-primary">{data.total_promotions || 0}</p>
                  <p className="text-xs text-gray-medium">Active Promotions</p>
                </CardContent>
              </Card>
            </div>
          </>
        );

      case 'customer-service':
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Total Tickets" value={data.total_tickets} icon={HeadphonesIcon} iconColor="#3B82F6" />
              <KPI label="Open" value={data.open_tickets} icon={Clock} iconColor="#F59E0B" sub={`${data.in_progress_tickets} in progress`} />
              <KPI label="Resolution Rate" value={`${data.resolution_rate}%`} icon={CheckCircle2} iconColor="#10B981" />
              <KPI label="Live Chats" value={data.total_chats} icon={MessageSquare} iconColor="#8B5CF6" sub={`${data.active_chats} active`} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">By Priority</CardTitle></CardHeader>
                <CardContent>
                  <MiniBar items={Object.entries(data.by_priority || {}).map(([k, v]) => ({ label: k, count: v }))} colorMap={['#EF4444', '#F59E0B', '#3B82F6', '#6B7280']} />
                  {Object.keys(data.by_priority || {}).length === 0 && <p className="text-xs text-gray-medium text-center py-2">No data</p>}
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                    <span className="text-xs font-medium text-emerald-700">Resolved</span>
                    <span className="text-sm font-bold text-emerald-700">{data.resolved_tickets}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-xs font-medium text-blue-700">KB Articles</span>
                    <span className="text-sm font-bold text-blue-700">{data.kb_articles}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-xs font-medium text-purple-700">Surveys</span>
                    <span className="text-sm font-bold text-purple-700">{data.total_surveys}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        );

      case 'customer-retention':
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Loyalty Members" value={data.loyalty_members} icon={Heart} iconColor="#EC4899" />
            <KPI label="Referrals" value={data.total_referrals} icon={Users} iconColor="#10B981" />
            <KPI label="Satisfaction" value={`${data.satisfaction_rate}%`} icon={TrendingUp} iconColor="#F59E0B" sub={`${data.positive_reviews}/${data.total_reviews} positive`} />
            <KPI label="Winback" value={data.winback_campaigns} icon={Target} iconColor="#8B5CF6" sub={`${data.celebrations_scheduled} celebrations`} />
          </div>
        );

      case 'operations':
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Staff" value={data.total_staff} icon={Users} iconColor="#10B981" sub={`${data.active_staff} active`} />
              <KPI label="Tasks" value={data.total_tasks} icon={CheckCircle2} iconColor="#3B82F6" sub={`${data.task_completion_rate}% complete`} />
              <KPI label="KPI Progress" value={`${data.avg_kpi_progress}%`} icon={TrendingUp} iconColor="#8B5CF6" sub={`${data.total_kpis} tracked`} />
              <KPI label="Documents" value={data.total_documents} icon={FileText} iconColor="#F59E0B" />
            </div>
            <div className="mt-4">
              <Card className="bg-white border-gray-border">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-text-primary mb-2">Task Completion</p>
                  <Progress value={data.task_completion_rate || 0} className="h-3" />
                  <div className="flex justify-between mt-2 text-xs text-gray-medium">
                    <span>{data.completed_tasks} completed</span>
                    <span>{data.in_progress_tasks} in progress</span>
                    <span>{data.total_tasks - data.completed_tasks - data.in_progress_tasks} pending</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        );

      case 'proposals':
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI label="Total Proposals" value={data.total} icon={FileText} iconColor="#3B82F6" />
              <KPI label="Accepted" value={data.accepted} icon={CheckCircle2} iconColor="#10B981" sub={`${data.acceptance_rate}% rate`} />
              <KPI label="Rejected" value={data.rejected} icon={Target} iconColor="#EF4444" sub={`${data.rejection_rate}% rate`} />
              <KPI label="Accepted Value" value={fc(data.accepted_value || 0)} icon={DollarSign} iconColor="#F59E0B" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Status Pipeline</CardTitle></CardHeader>
                <CardContent>
                  <MiniBar
                    items={[
                      { label: 'Draft', count: data.draft },
                      { label: 'Sent', count: data.sent },
                      { label: 'Accepted', count: data.accepted },
                      { label: 'Rejected', count: data.rejected },
                    ]}
                    colorMap={['#6B7280', '#3B82F6', '#10B981', '#EF4444']}
                  />
                </CardContent>
              </Card>
              <Card className="bg-white border-gray-border">
                <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Monthly Trend</CardTitle></CardHeader>
                <CardContent>
                  {(data.monthly_trend || []).length > 0 ? (
                    <MiniBar items={data.monthly_trend.map(m => ({ label: m.month, count: m.count }))} colorMap={['#3B82F6']} />
                  ) : (
                    <p className="text-xs text-gray-medium text-center py-2">No data</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-5 w-5" style={{ color: iconColor }} />
        <h2 className="text-lg font-heading font-semibold text-text-primary">{title}</h2>
      </div>
      {renderContent()}
    </div>
  );
};


// ─── Main Page ───
export default function ManagementHubPage() {
  const { format: fc } = useCurrency();
  const [dashboards, setDashboards] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const endpoints = ['lead-generation', 'sales', 'marketing', 'customer-service', 'customer-retention', 'operations', 'proposals', 'overview'];
      const results = await Promise.all(
        endpoints.map(ep => axios.get(`${API}/api/dashboards/${ep}`).catch(() => ({ data: null })))
      );
      const d = {};
      endpoints.forEach((ep, i) => { d[ep] = results[i].data; });
      setDashboards(d);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-emerald-brand" /></div>;

  const overview = dashboards.overview || {};

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="management-hub-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-text-primary">Management Hub<PageInfoTooltip pageKey="management-hub" /></h1>
        <p className="text-gray-medium">Cross-module KPIs and analytics — all activities quantified</p>
      </div>

      {/* Global KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="global-kpis">
        <KPI label="Contacts" value={overview.contacts || 0} icon={Users} iconColor="#10B981" />
        <KPI label="Deals" value={overview.deals || 0} icon={DollarSign} iconColor="#3B82F6" sub={`${overview.deal_win_rate || 0}% win`} />
        <KPI label="Proposals" value={overview.proposals || 0} icon={FileText} iconColor="#8B5CF6" sub={`${overview.proposal_acceptance_rate || 0}% accepted`} />
        <KPI label="Tickets" value={overview.tickets || 0} icon={HeadphonesIcon} iconColor="#F59E0B" sub={`${overview.ticket_resolution_rate || 0}% resolved`} />
        <KPI label="Staff" value={overview.staff || 0} icon={Users} iconColor="#EC4899" />
        <KPI label="Tasks" value={overview.tasks || 0} icon={CheckCircle2} iconColor="#06B6D4" sub={`${overview.task_completion_rate || 0}% done`} />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="lead-generation" className="space-y-6">
        <TabsList className="bg-white border border-gray-border w-full overflow-x-auto flex-nowrap justify-start">
          <TabsTrigger value="lead-generation" className="text-xs whitespace-nowrap">Lead Gen</TabsTrigger>
          <TabsTrigger value="sales" className="text-xs whitespace-nowrap">Sales</TabsTrigger>
          <TabsTrigger value="marketing" className="text-xs whitespace-nowrap">Marketing</TabsTrigger>
          <TabsTrigger value="customer-service" className="text-xs whitespace-nowrap">Service</TabsTrigger>
          <TabsTrigger value="customer-retention" className="text-xs whitespace-nowrap">Retention</TabsTrigger>
          <TabsTrigger value="operations" className="text-xs whitespace-nowrap">Operations</TabsTrigger>
          <TabsTrigger value="proposals" className="text-xs whitespace-nowrap">Proposals</TabsTrigger>
        </TabsList>

        <TabsContent value="lead-generation">
          <SectionDashboard title="Lead Generation" icon={Users} iconColor="#10B981" data={dashboards['lead-generation']} type="lead-generation" />
        </TabsContent>
        <TabsContent value="sales">
          <SectionDashboard title="Sales" icon={DollarSign} iconColor="#3B82F6" data={dashboards.sales} type="sales" />
        </TabsContent>
        <TabsContent value="marketing">
          <SectionDashboard title="Marketing" icon={Megaphone} iconColor="#8B5CF6" data={dashboards.marketing} type="marketing" />
        </TabsContent>
        <TabsContent value="customer-service">
          <SectionDashboard title="Customer Service" icon={HeadphonesIcon} iconColor="#F59E0B" data={dashboards['customer-service']} type="customer-service" />
        </TabsContent>
        <TabsContent value="customer-retention">
          <SectionDashboard title="Customer Retention" icon={Heart} iconColor="#EC4899" data={dashboards['customer-retention']} type="customer-retention" />
        </TabsContent>
        <TabsContent value="operations">
          <SectionDashboard title="Operations" icon={Settings} iconColor="#06B6D4" data={dashboards.operations} type="operations" />
        </TabsContent>
        <TabsContent value="proposals">
          <SectionDashboard title="Proposals" icon={FileText} iconColor="#8B5CF6" data={dashboards.proposals} type="proposals" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
